import { createFileRoute } from "@tanstack/react-router";
import { TableLoaderRow } from "@/components/ui/loader";
import { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { useDisposalsFinance } from "@/features/finance/api";
import { currentFy, fyOptions } from "@/features/finance/fy";
import { inr } from "@/features/finance/format";
import { useBranch } from "@/context/BranchContext";

export const Route = createFileRoute("/_authenticated/finance/disposals")({
  component: DisposalsFinance,
});

function DisposalsFinance() {
  const [fy, setFy] = useState(currentFy());
  const { currentBranchId } = useBranch();
  const { data, isLoading } = useDisposalsFinance(fy, currentBranchId);
  const rows = data ?? [];

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">Disposals Finance</h1>
          <p className="text-sm text-muted-foreground">NBV, sale value, GST and P/L for disposed assets in FY {fy}.</p>
        </div>
        <Select value={fy} onValueChange={setFy}>
          <SelectTrigger className="w-36"><SelectValue /></SelectTrigger>
          <SelectContent>{fyOptions().map((y) => <SelectItem key={y} value={y}>FY {y}</SelectItem>)}</SelectContent>
        </Select>
      </div>
      <Card>
        <CardHeader><CardTitle className="text-base">Disposed assets</CardTitle></CardHeader>
        <CardContent>
          <Table>
            <TableHeader><TableRow>
              <TableHead>Code</TableHead><TableHead>Asset</TableHead><TableHead>Date</TableHead>
              <TableHead>Mode</TableHead><TableHead>Buyer</TableHead>
              <TableHead className="text-right">NBV</TableHead>
              <TableHead className="text-right">Sale</TableHead>
              <TableHead className="text-right">GST</TableHead>
              <TableHead className="text-right">P/L</TableHead>
            </TableRow></TableHeader>
            <TableBody>
              {isLoading && <TableLoaderRow colSpan={9} />}
              {rows.map((r) => (
                <TableRow key={r.id}>
                  <TableCell className="font-mono text-xs">{r.asset_code}</TableCell>
                  <TableCell>{r.asset_name}</TableCell>
                  <TableCell>{r.disposal_date}</TableCell>
                  <TableCell>{r.mode}</TableCell>
                  <TableCell>{r.buyer_name ?? "—"}</TableCell>
                  <TableCell className="text-right">{inr(r.nbv)}</TableCell>
                  <TableCell className="text-right">{inr(r.sale_value)}</TableCell>
                  <TableCell className="text-right">{inr(r.gst_on_sale)}</TableCell>
                  <TableCell className={`text-right font-medium ${r.profit_loss >= 0 ? "text-emerald-600" : "text-red-600"}`}>{inr(r.profit_loss)}</TableCell>
                </TableRow>
              ))}
              {!isLoading && rows.length === 0 && <TableRow><TableCell colSpan={9} className="text-center text-muted-foreground">No disposals in this FY</TableCell></TableRow>}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
}
